import { Component, OnInit } from '@angular/core';
import { IdfReportService } from 'src/app/services/reports/idf-report.service';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalService } from 'src/app/services/shared/global.service';
import { ConsigneeService } from 'src/app/services/consignees/consignee.service';
import { AgentService } from 'src/app/services/agents/agent.service';
import { ApiResponse } from 'src/app/models/IApiResponse';

@Component({
  selector: 'app-idf-report',
  templateUrl: './idf-report.component.html',
  styleUrls: ['./idf-report.component.css']
})
export class IdfReportComponent implements OnInit{
  public errorMessage: string | null = null;
    idf_data: any[] = [];
    total: number = 0;
    public isInProgress: boolean = false;
    form!: FormGroup;
    submitted: boolean = false;
    startDate!: string;
    endDate!: string;
    consignees: any[] = [];
    agents: any[] = [];

    constructor(
      private idfReportService:IdfReportService,
      private globalService: GlobalService,
      private consigneeService: ConsigneeService,
      private agentService: AgentService,
      private formBuilder: FormBuilder,
    ){
      this.form = this.formBuilder.group({
        date_from: ['', Validators.required],
        date_to: ['', Validators.required],
        consignee_id:[''],
        agent_id:['']
      });
    }

    ngOnInit(): void {
      const today = new Date();
      const formatted = today.toISOString().split('T')[0];

      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const formattedStartOfMonth = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-01`;

      this.startDate = formattedStartOfMonth;
      this.endDate = formatted;

      this.isInProgress=true;
      this.getConsignees();
      this.fetchReportData();
      this.getAgents();
    }

    getConsignees(): void {
      this.consigneeService.getAllConsignees().subscribe((response: ApiResponse)=>this.consignees=response.data);
    }

    getAgents(): void{
      this.agentService.getAllAgents().subscribe((response: ApiResponse) =>this.agents = response.data);
    }

    fetchReportData():void{
      this.idfReportService.getData()
      .subscribe({
        next: (response: any) => {
          if(response.status==='success'){
            this.idf_data = response.data;

            // ✅ Calculate total
            this.total = this.idf_data.reduce(
              (sum, item) => sum + Number(item.amount),
              0
            );

            this.isInProgress=false;
          }else if(response.status==='no_data'){
             this.globalService.setGlobalWarningMessage(response.message);
             this.isInProgress=false;
          }else{
            this.errorMessage="Sorry, a problem was encountered while processing your request. Please refresh the page and try again!";
            this.globalService.setGlobalErrorMessage(this.errorMessage);
            this.isInProgress=false;
          }
        },
        error: (error) => {
          console.log(error)
          this.globalService.setGlobalErrorMessage(error);
          this.isInProgress=false;
        }
      });
    }

    filter(){
      this.submitted=true;
      this.isInProgress = true;

      if (this.form.valid) {
        this.startDate = this.form.value.date_from;
        this.endDate = this.form.value.date_to;

       this.idfReportService.filter(this.form.value.date_from,this.form.value.date_to,this.form.value.consignee_id,this.form.value.agent_id)
        .subscribe({
          next: (response: any) => {
            if(response.status==='success'){
              this.idf_data = response.data;

              // ✅ Calculate total
              this.total = this.idf_data.reduce(
                (sum, item) => sum + Number(item.amount),
                0
              );

              this.isInProgress=false;
            }else if(response.status==='no_data'){
              this.isInProgress=false;
              this.idf_data=[];
              this.globalService.setGlobalWarningMessage(response.message);
            }else{
              this.isInProgress=false;
              const errorMessage="Sorry, a problem was encountered while filtering sales report. Please try again!";
              this.globalService.setGlobalErrorMessage(errorMessage);
            }
          },
          error: (error) => {
            this.isInProgress=false;
            this.globalService.setGlobalErrorMessage(error);
          }
        });
      }else{
        this.isInProgress = false;
      }
    }

    exportToPdf() {
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Title
      const title = `IDF Report for the period from ${this.formatPdfDate(
        this.startDate
      )} to ${this.formatPdfDate(this.endDate)}`;

      pdf.setFontSize(14);
      pdf.text(title, 105, 15, { align: 'center' });

      autoTable(pdf, {
        html: '#idfTable',
        theme: 'striped',
        startY: 25,
        headStyles: { fillColor: [40, 167, 69], textColor: 255 },
        styles: { fontSize: 9, cellPadding: 3 },
        didDrawPage: (data) => this.drawFooter(pdf)
      });

      pdf.save('sales-report.pdf');
    }

    drawFooter(pdf: jsPDF) {
      const pageCount = pdf.getNumberOfPages();
      const pageSize = pdf.internal.pageSize;
      const pageHeight = pageSize.height || pageSize.getHeight();

      // Page number
      pdf.setFontSize(10);
      pdf.text(
        `Page ${pdf.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageSize.width / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      // Generated date
      const now = new Date();
      const formatted = now.toLocaleString('en-GB');

      pdf.text(`Generated On: ${formatted}`, 10, pageHeight - 10);
    }

    formatPdfDate(dateStr: string): string {
      const d = new Date(dateStr);

      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    }

    exportToExcel(){
      this.isInProgress = true;
      this.idfReportService.exportToExcel(this.form.value.date_from,this.form.value.date_to,this.form.value.consignee_id,this.form.value.agent_id)
    .subscribe({
      next: (response: Blob) => {
        this.isInProgress = false;

        // Create a download link for the file
        const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'IDF-REPORT.xlsx'; // Set filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        this.globalService.setGlobalSuccessMessage("Stock exported successfully!");
        this.errorMessage=null;
      },
      error: (error) => {
        this.isInProgress = false;
        this.errorMessage="Export failed. Please try again!";
        this.globalService.setGlobalErrorMessage(this.errorMessage);
        console.error("Download error:", error);
      }
    });
  }
}
